import { Component, type ErrorInfo, type ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Top-level catch-all — without this, any uncaught render-time exception
 * anywhere in the app unmounts the whole React tree and leaves a blank white
 * screen with zero feedback (confirmed root cause of the "Lỗi đăng ký – tạo
 * hồ sơ: Màn hình trắng tinh" report). This does not fix the underlying bugs,
 * it just makes any future one fail visibly instead of silently.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Uncaught render error:', error, info.componentStack);
  }

  private reset = () => {
    this.setState({ error: null });
    window.location.href = '/';
  };

  render() {
    if (this.state.error) {
      return (
        <div className="flex min-h-[100dvh] flex-col items-center justify-center gap-4 bg-white px-6 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-red-50">
            <i className="ri-error-warning-line text-3xl text-red-500" aria-hidden="true" />
          </span>
          <div>
            <h1 className="text-lg font-bold text-slate-900">Đã xảy ra lỗi</h1>
            <p className="mt-1 max-w-sm text-sm text-slate-500">
              Có lỗi không mong muốn xảy ra khi hiển thị trang này. Vui lòng thử tải lại.
            </p>
          </div>
          <button
            type="button"
            onClick={this.reset}
            className="min-h-11 rounded-xl bg-agrida-600 px-5 text-sm font-bold text-white hover:bg-agrida-700"
          >
            Tải lại trang
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
